<template>
  <div>
    <!-- Botones sobre la tabla: los de la vista (#actions) y luego columnas visibles -->
    <div class="d-flex justify-content-end align-items-center mb-2 noprint">
      <slot name="actions"></slot>
      <AcademicLoadColumnsSelector
        :time_allocations="timeAllocations"
        :is_visible="isVisible"
        :hidden_count="hiddenCount"
        @change="setVisible"
        @show-all="showAll"
      ></AcademicLoadColumnsSelector>
    </div>
    <div class="table-responsive academic-load-container">
      <table class="table table-sm table-bordered academic-load-table">
        <thead>
          <tr>
            <th
              v-for="header in fixedHeaders"
              :key="header.key"
              :rowspan="headerRowspan"
              class="fixed-header"
            >
              {{ header.label }}
            </th>
            <th
              v-if="visibleColumns.hours.length > 0"
              :colspan="visibleColumns.hours.length"
              class="group-header"
            >
              {{ $t("studyPlan.academicLoad.hoursDetail") }}
            </th>
            <th
              v-if="totalHeaders.length > 0"
              :colspan="totalHeaders.length"
              class="group-header"
            >
              {{ $t("studyPlan.academicLoad.hoursTotals") }}
            </th>
            <th
              v-for="group in allocationGroups"
              :key="group.key"
              :colspan="group.allocations.length"
              class="group-header"
            >
              {{ group.label }}
            </th>
            <th
              v-for="header in noteHeaders"
              :key="header.key"
              :rowspan="headerRowspan"
              class="fixed-header"
            >
              {{ header.label }}
            </th>
          </tr>
          <tr v-if="headerRowspan == 2">
            <th
              v-for="time_allocation in visibleColumns.hours"
              :key="`hours-${time_allocation.id}`"
            >
              {{ time_allocation.name }}
            </th>
            <th v-for="header in totalHeaders" :key="header.key">
              {{ header.label }}
            </th>
            <template v-for="group in allocationGroups">
              <th
                v-for="time_allocation in group.allocations"
                :key="`${group.key}-${time_allocation.id}`"
              >
                {{ time_allocation.name }}
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <AcademicLoadRow
            v-for="egress_profile_matter in profileMatters"
            :key="egress_profile_matter.id"
            :egress_profile_matter="egress_profile_matter"
            :matter="matterOf(egress_profile_matter)"
            :time_allocations="timeAllocations"
            :visible_columns="visibleColumns"
            :allocations="allocationsOf(egress_profile_matter)"
            :classroom_types="classroomTypes"
            :module_minutes="moduleMinutes"
            :semester_amount="semesterAmount"
            :prerequisites_label="prerequisitesLabel(egress_profile_matter)"
            :allows_crud="allows_crud"
            @edit-prerequisites="
              (matter) => (editing_prerequisites_matter = matter)
            "
          ></AcademicLoadRow>
          <tr v-if="profileMatters.length == 0">
            <td :colspan="columnCount" class="text-center">
              {{ $t("studyPlan.academicLoad.noMatters") }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <AcademicLoadPrerequisitesModal
      :egress_profile_matter="editingPrerequisitesMatter"
      :profile_matters="profileMatters"
      :matters="matters"
      @close="editing_prerequisites_matter = null"
    ></AcademicLoadPrerequisitesModal>
  </div>
</template>

<script setup>
// eslint-plugin-vue 6.x (anterior a <script setup>) no ve que estos bindings
// se usan en el template. Quitar el disable cuando se actualice el tooling.
/* eslint-disable no-unused-vars */
// Tabla de la Carga Académica de un perfil de egreso, con las columnas de la hoja
// "Carga Academica" del Anexo 4 PUCV. Las columnas por División de tiempo se repiten en
// cada grupo (horas, cupo, tipo de sala y periodicidad), igual que en el Excel. Las columnas
// se pueden ocultar con AcademicLoadColumnsSelector (solo en pantalla; el Excel va completo).
import { computed, getCurrentInstance, ref } from "vue";
import * as names from "@/store/names";
import { allocationsOfMatter } from "@/utils/academicLoad";
import AcademicLoadRow from "./AcademicLoadRow";
import AcademicLoadPrerequisitesModal from "./AcademicLoadPrerequisitesModal";
import AcademicLoadColumnsSelector from "./AcademicLoadColumnsSelector";
import useAcademicLoadColumns from "./useAcademicLoadColumns";

const props = defineProps({
  egress_profile_id: {
    type: Number,
    required: true,
  },
  allows_crud: {
    type: Boolean,
    default: false,
  },
});

const proxy = getCurrentInstance().proxy;
const store = proxy.$store;
const t = (key) => proxy.$t(`studyPlan.academicLoad.${key}`);

const institution = computed(() => store.getters.getInstitution);
const egressProfile = computed(() =>
  store.getters[names.EGRESS_PROFILES].find(
    (x) => x.id == props.egress_profile_id
  )
);
const profileType = computed(() =>
  egressProfile.value
    ? store.getters[names.PROFILE_TYPES].find(
        (x) => x.id == egressProfile.value.egress_profile_type
      )
    : null
);
const moduleMinutes = computed(() =>
  profileType.value ? profileType.value.module_minutes : 0
);
const semesterAmount = computed(() =>
  egressProfile.value ? egressProfile.value.semester_amount || 1 : 1
);

const matters = computed(() => store.getters[names.MATTERS]);
const timeAllocations = computed(() => store.getters[names.TIME_ALLOCATIONS]);
const classroomTypes = computed(() => store.getters[names.CLASSROOM_TYPES]);
const matterTimeAllocations = computed(
  () => store.getters[names.MATTER_TIME_ALLOCATIONS]
);
const epMatterTimeAllocations = computed(
  () => store.getters[names.EP_MATTER_TIME_ALLOCATIONS]
);

// Ordenadas como el Excel: por semestre y luego por el orden del Plan de Estudio
const profileMatters = computed(() =>
  store.getters[names.EGRESS_PROFILES_MATTERS]
    .filter((x) => x.egress_profile == props.egress_profile_id)
    .sort(
      (a, b) => a.plan_level - b.plan_level || a.order - b.order || a.id - b.id
    )
);

const user = computed(() => store.getters.getUser);
const columnsStorageKey = computed(() =>
  user.value && institution.value
    ? `academic_load_hidden_columns_${user.value.id}_${institution.value.id}`
    : null
);
const { isVisible, setVisible, showAll, visibleColumns, hiddenCount } =
  useAcademicLoadColumns(timeAllocations, columnsStorageKey);

// column: clave de useAcademicLoadColumns; sin column la columna siempre se ve (Nombre)
function visibleHeaders(headers) {
  return headers.filter(
    (header) => !header.column || visibleColumns.value[header.column]
  );
}

const fixedHeaders = computed(() =>
  visibleHeaders([
    { key: "period", column: "period", label: t("period") },
    { key: "internal_code", column: "internal_code", label: t("internalCode") },
    { key: "code", column: "code", label: t("code") },
    { key: "name", label: t("name") },
    {
      key: "prerequisites",
      column: "prerequisites",
      label: t("prerequisites"),
    },
    { key: "pucv_credits", column: "pucv_credits", label: t("pucvCredits") },
    { key: "sct_credits", column: "sct_credits", label: t("sctCredits") },
  ])
);
// Cada total visible aporta su columna semanal y la semestral
const totalHeaders = computed(() =>
  visibleHeaders([
    {
      key: "presential_weekly",
      column: "presential",
      label: t("presentialWeekly"),
    },
    {
      key: "presential_semester",
      column: "presential",
      label: t("presentialSemester"),
    },
    {
      key: "autonomous_weekly",
      column: "autonomous",
      label: t("autonomousWeekly"),
    },
    {
      key: "autonomous_semester",
      column: "autonomous",
      label: t("autonomousSemester"),
    },
    {
      key: "dedication_weekly",
      column: "dedication",
      label: t("dedicationWeekly"),
    },
    {
      key: "dedication_semester",
      column: "dedication",
      label: t("dedicationSemester"),
    },
  ])
);
// Grupos después de los totales; Detalle Horas va antes y se arma en el template
const allocationGroups = computed(() =>
  [
    { key: "quota", label: t("maximumQuota") },
    { key: "classroom_type", label: t("classroomType") },
    { key: "periodicity", label: t("periodicity") },
  ]
    .map((group) => ({
      ...group,
      allocations: visibleColumns.value[group.key],
    }))
    .filter((group) => group.allocations.length > 0)
);
const noteHeaders = computed(() =>
  visibleHeaders([
    {
      key: "quota_justification",
      column: "quota_justification",
      label: t("quotaJustification"),
    },
    {
      key: "observation",
      column: "academic_load_observation",
      label: t("observation"),
    },
  ])
);
const subHeaderCount = computed(
  () =>
    visibleColumns.value.hours.length +
    totalHeaders.value.length +
    allocationGroups.value.reduce(
      (count, group) => count + group.allocations.length,
      0
    )
);
// Sin columnas agrupadas visibles no hay segunda fila de encabezado
const headerRowspan = computed(() => (subHeaderCount.value > 0 ? 2 : 1));
const columnCount = computed(
  () =>
    fixedHeaders.value.length + subHeaderCount.value + noteHeaders.value.length
);

function matterOf(egress_profile_matter) {
  return (
    matters.value.find((x) => x.id == egress_profile_matter.matter) || null
  );
}

function allocationsOf(egress_profile_matter) {
  return allocationsOfMatter(
    egress_profile_matter,
    matterTimeAllocations.value,
    epMatterTimeAllocations.value
  );
}

function prerequisitesLabel(egress_profile_matter) {
  return profileMatters.value
    .filter((x) => egress_profile_matter.prerequisites.includes(x.id))
    .map((x) => x.effective_code)
    .join(", ");
}

// Se guarda el id y se busca en el store para que el modal vea los pre-requisitos vigentes
const editing_prerequisites_matter = ref(null);
const editingPrerequisitesMatter = computed(() =>
  editing_prerequisites_matter.value
    ? profileMatters.value.find(
        (x) => x.id == editing_prerequisites_matter.value.id
      ) || null
    : null
);

const egress_profile_filter = { egress_profile_id: props.egress_profile_id };
store.dispatch(names.FETCH_PROFILE_TYPES);
store.dispatch(names.FETCH_TIME_ALLOCATIONS);
store.dispatch(names.FETCH_CLASSROOM_TYPES);
store.dispatch(names.FETCH_MATTERS, egress_profile_filter);
store.dispatch(names.FETCH_EGRESS_PROFILES_MATTERS, egress_profile_filter);
store.dispatch(names.FETCH_MATTER_TIME_ALLOCATIONS, egress_profile_filter);
store.dispatch(names.FETCH_EP_MATTER_TIME_ALLOCATIONS, egress_profile_filter);
</script>

<style scoped>
.academic-load-container {
  max-height: 75vh;
  overflow: auto;
}
.academic-load-table {
  margin-bottom: 0;
}
th {
  vertical-align: middle;
  text-align: center;
  font-weight: var(--kl-fw-bold);
  font-size: var(--secondary-font-size);
  background-color: var(--kl-color-accent-medium);
  color: var(--kl-text-on-primary);
  min-width: 5rem;
}
.group-header {
  background-color: var(--kl-fourth-color, var(--kl-color-accent-medium));
}
</style>
